'use client'

import { useI18N } from '@/context/I18NContext'
import { formatSupportedFileTypes } from '@/lib/formatSupportedFileTypes'
import { useMutation } from '@tanstack/react-query'
import { FileUpload } from '../FileUpload'
import { getUploadParams } from './actions'

const SUPPORTED_FILE_TYPES = {
	'text/xml': '.xml',
	'application/zip': '.zip',
	'application/pgp-encrypted': '.zip', // although we support .gpg files we do not want regular users to know this
}

export interface SaftUploadProps {
	onSuccess: (fileId: string) => void
	isPending?: boolean
	isSuccess?: boolean
	error?: Error | null
	reset?: () => void
}

export const SaftUpload: React.FC<SaftUploadProps> = ({
	onSuccess,
	isPending: providedIsPending,
	isSuccess: providedIsSuccess,
	error: providedError,
	reset: providedReset,
}) => {
	const { getTranslation } = useI18N()

	const {
		mutate,
		isPending,
		isSuccess,
		error: fileError,
		reset,
	} = useMutation({
		mutationFn: async (file: File) => {
			const supportedFormatsString =
				formatSupportedFileTypes(SUPPORTED_FILE_TYPES)
			if (file.type in SUPPORTED_FILE_TYPES === false && file.type !== '') {
				throw new Error(
					getTranslation('forms.supportedFileTypes', {
						supported: supportedFormatsString,
					}),
				)
			}

			const { id, url, key, keySha256 } = await getUploadParams(file.type)
			await fetch(url, {
				method: 'PUT',
				headers: {
					'Content-Type': file.type,
					'x-goog-encryption-algorithm': 'AES256',
					'x-goog-encryption-key': key,
					'x-goog-encryption-key-sha256': keySha256,
				},
				body: file,
			})
			return id
		},
		onSuccess: (id) => {
			if (!id) return

			onSuccess(id)
		},
	})

	function resetAll() {
		reset?.()
		providedReset?.()
	}

	const label = getTranslation('forms.uploadSaft')
	const error = providedError || fileError
	const errorMessage = error
		? getTranslation('forms.uploadError', {
				message: error.message,
			})
		: null

	return (
		<FileUpload
			onChange={mutate}
			label={label}
			loading={providedIsPending || isPending}
			success={providedIsSuccess && isSuccess}
			error={errorMessage}
			reset={resetAll}
			supportedFileTypes={SUPPORTED_FILE_TYPES}
		/>
	)
}
